import L from 'leaflet';
import { GpsCoordinates } from '@sos/shared';

export class InteractiveMap {
  private map: L.Map | null = null;
  private marker: L.Marker | null = null;
  private accuracyCircle: L.Circle | null = null;
  private pathPolyline: L.Polyline | null = null;
  private pathCoordinates: L.LatLngExpression[] = [];
  private telemetryElement: HTMLElement | null = null;
  private popupWindow: Window | null = null;

  constructor(private readonly containerId: string) {}

  public init(initialLat: number = 21.8853, initialLng: number = -102.2916): void {
    const container = document.getElementById(this.containerId);
    if (!container) return;

    // Inicializar mapa de Leaflet con coordenadas iniciales por defecto
    this.map = L.map(this.containerId, {
      zoomControl: true,
      attributionControl: false
    }).setView([initialLat, initialLng], 15);

    // Capa base de OpenStreetMap estándar
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map);

    // Icono de emergencias personalizado con pulso SVG
    const pulseIcon = L.divIcon({
      className: 'emergency-map-marker',
      html: `
        <div style="position:relative; width:30px; height:30px;">
          <div style="position:absolute; inset:0; border-radius:50%; background:rgba(239,68,68,0.4); animation: pulse-ring 1.5s infinite;"></div>
          <div style="position:absolute; inset:6px; border-radius:50%; background:#ef4444; border:2px solid #fff; box-shadow:0 0 10px #ef4444;"></div>
        </div>
      `,
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    });

    this.marker = L.marker([initialLat, initialLng], { icon: pulseIcon }).addTo(this.map);
    this.accuracyCircle = L.circle([initialLat, initialLng], {
      radius: 20,
      color: '#ef4444',
      fillColor: '#ef4444',
      fillOpacity: 0.15,
      weight: 1
    }).addTo(this.map);

    this.pathPolyline = L.polyline([], {
      color: '#3b82f6',
      weight: 3,
      opacity: 0.8,
      dashArray: '4, 8'
    }).addTo(this.map);

    // Forzar redibujado de tamaño
    setTimeout(() => {
      this.map?.invalidateSize();
    }, 200);
  }

  public setTelemetryElement(el: HTMLElement): void {
    this.telemetryElement = el;
  }

  public updatePosition(coords: GpsCoordinates): void {
    if (!this.map || !this.marker || !this.accuracyCircle || !this.pathPolyline) return;

    const latLng: [number, number] = [coords.latitude, coords.longitude];

    this.marker.setLatLng(latLng);
    this.accuracyCircle.setLatLng(latLng);
    this.accuracyCircle.setRadius(coords.accuracy);

    // Agregar a la ruta recorrida
    this.pathCoordinates.push(latLng);
    this.pathPolyline.setLatLngs(this.pathCoordinates);

    // Centrar suavemente
    this.map.panTo(latLng);

    // Actualizar telemetría visual con aviso de calidad GPS
    if (this.telemetryElement) {
      const speedKmH = coords.speed !== null && coords.speed !== undefined
        ? Math.round(coords.speed * 3.6)
        : 0;

      const isHighPrecision = coords.accuracy <= 25;
      const precisionBadge = isHighPrecision
        ? `<span style="color:#34d399; font-weight:700;">🎯 Satélite GPS (±${coords.accuracy}m)</span>`
        : `<span style="color:#f59e0b; font-weight:700;">📡 WiFi/Antena (±${coords.accuracy}m - esperando satélite)</span>`;

      this.telemetryElement.innerHTML = `
        <span>📍 ${coords.latitude.toFixed(5)}, ${coords.longitude.toFixed(5)}</span>
        ${precisionBadge}
        ${speedKmH > 0 ? `<span>⚡ ${speedKmH} km/h</span>` : ''}
      `;
    }

    // Sincronizar en tiempo real con la ventana emergente si está abierta
    if (this.popupWindow && !this.popupWindow.closed) {
      try {
        (this.popupWindow as any).updateGps?.(
          coords.latitude,
          coords.longitude,
          coords.accuracy,
          coords.speed
        );
      } catch (_) {}
    }
  }

  public resetPath(): void {
    this.pathCoordinates = [];
    this.pathPolyline?.setLatLngs([]);
  }

  public invalidateSize(): void {
    this.map?.invalidateSize();
  }

  public openInPopupWindow(): Window | null {
    const currentCoords = this.marker?.getLatLng();
    const lat = currentCoords ? currentCoords.lat : 21.8853;
    const lng = currentCoords ? currentCoords.lng : -102.2916;

    this.popupWindow = window.open(
      '',
      'SOS_Map_Popup',
      'width=1000,height=750,menubar=no,toolbar=no,location=no,status=no,resizable=yes'
    );

    if (!this.popupWindow) {
      alert('Por favor permita ventanas emergentes (pop-ups) en su navegador.');
      return null;
    }

    this.popupWindow.document.write(`
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>SOS 911 - Mapa GPS de Escena (Ventana Emergente)</title>
        <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
        <style>
          body, html { margin:0; padding:0; height:100%; width:100%; background:#0f172a; font-family:sans-serif; }
          #popup-map { width:100%; height:100%; }
          .telemetry { position:absolute; bottom:16px; left:16px; z-index:1000; background:rgba(15,23,42,0.9); color:#fff; padding:8px 14px; border-radius:8px; border:1px solid #334155; font-family:monospace; font-size:13px; }
        </style>
      </head>
      <body>
        <div id="popup-map"></div>
        <div id="popup-telemetry" class="telemetry">📍 Lat: ${lat.toFixed(5)}, Lng: ${lng.toFixed(5)}</div>
        <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
        <script>
          const map = L.map('popup-map').setView([${lat}, ${lng}], 16);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
          const marker = L.marker([${lat}, ${lng}]).addTo(map).bindPopup('Ubicación del Reportante 911').openPopup();
          const path = L.polyline(${JSON.stringify(this.pathCoordinates)}, { color: '#3b82f6', weight: 4 }).addTo(map);

          window.updateGps = function(lat, lng, accuracy, speed) {
            marker.setLatLng([lat, lng]);
            map.panTo([lat, lng]);
            path.addLatLng([lat, lng]);
            document.getElementById('popup-telemetry').innerText = '📍 ' + lat.toFixed(5) + ', ' + lng.toFixed(5) + ' (±' + accuracy + 'm)';
          };
        </script>
      </body>
      </html>
    `);
    this.popupWindow.document.close();
    return this.popupWindow;
  }
}
